import { Document, Image, Page, Text, View } from '@react-pdf/renderer';
import PropTypes from 'prop-types';

import {
  handleArrayData,
  handleEnum,
} from 'components/ExportsPanel/ExportSelect/functions';
import { colors, CondizioniTransitabilitaL3Map } from 'constants/colors';
import { RischioIdraulico } from 'constants/inspections';
import {
  BacinoIdrograficoPresuntoRischioIdraulicoOptions,
  EffettivaPresuntaOptions,
  EsitoValutazioneL3Options,
  EvidenzeProfonditaDelleFondazioniOptions,
  EvoluzioneAlveoRischioIdraulicoOptions,
  MorfologiaAlveoOptions,
  ProtezioneAlPiedePileSpalleOptions,
  SinuositaOptions,
  StatoConservazioneProtezionePiedeOptions,
  StrutturaAlveoRischioIdraulicoOptions,
  tipoEnteScavalcatoOptions,
  TipologiaCorsoAcquaRischioIdraulicoOptions,
  TipologiaReticoloOptions,
  YesNoOptions,
} from 'constants/selectOptions';
import { formatData } from 'utils/utilities';
import { styles } from './SintesiL3Idraulico.styles';
import CalendarIcon from '../../../../assets/icons/calendar.png';
import LogoExport from '../../../../assets/icons/logoEsportazione.png';
import UserIcon from '../../../../assets/icons/user.png';
import { CdaColorMap } from '../SintesiCdAFrane/constants';

// Sintesi L3 Idraulico — task-30 (e-2026-06-t30), vedi
// apps/api/plans/features/task-30-handoff-fe-sintesi-cda-l3.md per il
// contratto completo. Layout allineato 1:1 al mockup cliente
// `plans/features/l3/Sintesi CdA L3 idraulico_rev1.pdf` (invariato in
// questo giro, solo ricablaggio dati): header con classe CdA, riga
// data/rilevatore, titolo+indirizzo, 2 foto, box "Dati Generali", box
// Sormonto/Erosione affiancati, box "Esiti" (609/610, specifico L3).
//
// `pdfData` è ora la risposta vera dell'export dedicato (GET
// exports/:bridgeId/hydraulic-l3-cda, gemella http della lambda type=115 —
// vedi useExportL3Idraulico.js), non più il mock BridgeL3OverviewDto: forma
// {cdaClasses, bridgeInfo, media, generalData, overlap, erosion, outcomes}.
// I campi non compilati sull'attività non compaiono affatto nel payload —
// l'operatore `?.` più i fallback `'-'' sotto bastano, nessuna gestione
// aggiuntiva.
//
// Numeri come stringhe: alcuni campi di `generalData`/`bridgeInfo`
// (luceComplessivaEstesa, luceIdraulicaMin/Max, altezzaUtileSottoImpalcato,
// larghezzaPilaOAggettoSpalla, progressivaKmIniziale) arrivano già
// formattati dal blob ("12.00", "23.000") e vanno stampati VERBATIM — mai
// passarli per parseFloat/Number(), perderebbero i decimali.
const SintesiL3Idraulico = ({ pdfData, formData }) => {
  const bridgeInfo = pdfData?.bridgeInfo;
  const media = pdfData?.media;
  const generalData = pdfData?.generalData;
  const overlap = pdfData?.overlap;
  const erosion = pdfData?.erosion;
  const outcomes = pdfData?.outcomes;
  const cdaClasses = pdfData?.cdaClasses;

  const enteScavalcato = handleArrayData(
    bridgeInfo?.tipologiaEnteScavalcato,
    tipoEnteScavalcatoOptions
  );

  // Dati Generali - Caratteristiche idrauliche
  const dimensioniBacino = generalData?.bacinoIdrograficoEffettivo
    ? `${generalData.bacinoIdrograficoEffettivo} Km`
    : handleEnum(
        BacinoIdrograficoPresuntoRischioIdraulicoOptions,
        generalData?.bacinoIdrograficoPresunto
      );
  const tipologiaReticolo = handleEnum(
    TipologiaReticoloOptions,
    generalData?.tipologiaReticolo
  );
  // ID119 + ID120 — sinuosità attivabile/mostrata solo per "A canale
  // singolo", separati da trattino come le altre coppie di questo
  // documento (159/159b, 171/172).
  const morgologiaAlveoBase = handleEnum(
    MorfologiaAlveoOptions,
    generalData?.morfologiaAlveo
  );
  const sinuosita = handleEnum(
    SinuositaOptions,
    generalData?.morfologiaAlveoCanaleSingolo
  );
  const morgologiaAlveo = morgologiaAlveoBase
    ? `${morgologiaAlveoBase}${sinuosita ? ` - ${sinuosita}` : ''}`
    : undefined;
  const evoluzioneAlveo = handleEnum(
    EvoluzioneAlveoRischioIdraulicoOptions,
    generalData?.evoluzioneAlveo
  );
  const tipologiaCorsoAcqua = handleEnum(
    TipologiaCorsoAcquaRischioIdraulicoOptions,
    generalData?.tipologiaCorsoAcqua
  );

  // Dati Generali - Struttura
  let luciIdrauliche;
  if (generalData?.luceIdraulicaMin && generalData?.luceIdraulicaMax) {
    if (generalData.luceIdraulicaMin === generalData.luceIdraulicaMax) {
      luciIdrauliche = `${generalData.luceIdraulicaMin} m`;
    } else {
      luciIdrauliche = `${generalData.luceIdraulicaMin} - ${generalData.luceIdraulicaMax} m`;
    }
  }

  // ID164 — compare sia in "Dati Generali > Struttura > Fondazioni" sia in
  // "Fenomeni di Erosione > Evidenze sulla profondità delle fondazioni":
  // stesso ID, ma ora due percorsi realmente distinti nel payload
  // (generalData.evidenzeProfonditaDelleFondazioni / erosion.stesso nome).
  const evidenzaProfonditaFondazioniGenerale = handleEnum(
    EvidenzeProfonditaDelleFondazioniOptions,
    generalData?.evidenzeProfonditaDelleFondazioni
  );
  const evidenzaProfonditaFondazioniErosione = handleEnum(
    EvidenzeProfonditaDelleFondazioniOptions,
    erosion?.evidenzeProfonditaDelleFondazioni
  );

  const partiInterferisconoAlveo = handleArrayData(
    generalData?.strutturaAlveo,
    StrutturaAlveoRischioIdraulicoOptions
  );

  // Fenomeno di Sormonto
  const francoIdraulico =
    overlap?.francoIdraulicoFP2 || overlap?.francoIdraulicoFP2 === 0
      ? `${parseFloat(overlap.francoIdraulicoFP2).toFixed(2)} m`
      : undefined;
  const evidenteDeposizioneSedimenti = handleEnum(
    YesNoOptions,
    overlap?.deposizioneSedimenti
  );
  const trasportoMassimoMaterialeVegetale = handleEnum(
    YesNoOptions,
    overlap?.materialeVegetale
  );

  // Fenomeni di Erosione
  const flagProfonditaFondazione = handleEnum(
    EffettivaPresuntaOptions,
    erosion?.profonditaFondazioneRispettoAlveoFlag
  );
  const protezioneAlPiede = handleEnum(
    ProtezioneAlPiedePileSpalleOptions,
    erosion?.protezioneAlPiedePileSpalle
  );
  // ID172 è `null` quando ID171 = "No" (attivabile solo con 171 = "Sì"): è
  // corretto, non un dato mancante — mostra solo "No"/"Sì", niente trattino
  // sospeso.
  const statoConservazioneProtezione = handleEnum(
    StatoConservazioneProtezionePiedeOptions,
    erosion?.statoConservazioneProtezionePiede
  );

  // Esiti — 609/610.
  const esitoValutazioneValue = outcomes?.esitoDellaValutazione;
  const esitiSelezionati = Array.isArray(esitoValutazioneValue)
    ? esitoValutazioneValue
    : [esitoValutazioneValue].filter(v => v !== undefined && v !== null);
  const condizioniTransitabilitaEntry = CondizioniTransitabilitaL3Map[
    outcomes?.condizioniTransitabilita
  ] ?? {
    labelColor: colors.SILVER,
    labelText: 'N.D.',
  };

  const renderStatusCdA = () => {
    const entry = CdaColorMap[cdaClasses?.hydraulic];
    if (entry) {
      return (
        <View
          style={[
            styles.infoTagGreyTitle,
            { backgroundColor: entry.labelColor },
          ]}
        >
          <Text style={styles.tagTextWhiteTitle}>{entry.labelText}</Text>
        </View>
      );
    }
    return (
      <View style={styles.infoTagGreyTitle}>
        <Text style={styles.tagTextWhiteTitle}>DA DEFINIRE</Text>
      </View>
    );
  };

  const renderRischioIdraulico = () => {
    if (bridgeInfo?.rischioIdraulico) {
      if (bridgeInfo.rischioIdraulico === RischioIdraulico.Presente) {
        return (
          <View style={styles.infoTagBlue}>
            <Text style={styles.tagText}>PRESENTE</Text>
          </View>
        );
      } else if (
        bridgeInfo.rischioIdraulico === RischioIdraulico.DaVerificare
      ) {
        return (
          <View style={styles.infoTagOutline}>
            <Text style={[styles.tagText, { color: 'black' }]}>
              DA VERIFICARE
            </Text>
          </View>
        );
      }
      return (
        <View style={styles.infoTagGrey}>
          <Text style={styles.tagText}>ASSENTE</Text>
        </View>
      );
    }
    return <Text style={styles.infoData}>{'-'}</Text>;
  };

  const renderCdATagInSection = cdaClass => {
    const entry = CdaColorMap[cdaClass] ?? CdaColorMap[6];
    return (
      <View
        style={[styles.infoTagFenomeni, { backgroundColor: entry.labelColor }]}
      >
        <Text style={styles.tagTextInSection}>{`CDA ${entry.labelText}`}</Text>
      </View>
    );
  };

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.wrapperCdA}>
          <Text style={{ fontSize: '12pt', fontFamily: 'Helvetica-Bold' }}>
            CDA IDRAULICA
          </Text>
          {renderStatusCdA()}
        </View>
        <View
          style={{
            marginTop: 8,
            flexDirection: 'row',
            justifyContent: 'flex-end',
          }}
        >
          <View style={styles.iconElementContainer}>
            <Image src={CalendarIcon} />
          </View>
          <Text style={styles.infoLabel}>
            {bridgeInfo?.dataAttivita
              ? formatData(bridgeInfo.dataAttivita)
              : '-'}
          </Text>
          <View style={[styles.iconElementContainer, { marginLeft: 10 }]}>
            <Image src={UserIcon} />
          </View>
          <Text style={[styles.infoLabel, { lineHeight: 0 }]}>
            {bridgeInfo?.rilevatore || '-'}
          </Text>
        </View>

        <View style={{ marginTop: '8pt' }}>
          <Text style={styles.title}>{bridgeInfo?.nomeOpera || '-'}</Text>
        </View>

        <View style={styles.flexContainer}>
          <Text style={styles.infoLabel}>Strada di appartenenza</Text>
          <Text style={styles.infoData}>
            {bridgeInfo?.stradaAppartenenza || '-'}
          </Text>
        </View>

        <View style={styles.flexContainer}>
          <Text style={styles.infoLabel}>Progressiva Km Iniziale</Text>
          <Text style={styles.infoData}>
            {bridgeInfo?.progressivaKmIniziale || '-'}
          </Text>
        </View>

        <View style={styles.flexContainer}>
          <Text style={styles.infoLabel}>Ente scavalcato</Text>
          <Text style={styles.infoData}>{enteScavalcato || '-'}</Text>
        </View>

        <View style={[styles.flexContainer, { marginTop: '6pt' }]}>
          <Text style={styles.infoLabel}>Rischio Idraulico</Text>
          {renderRischioIdraulico()}
        </View>

        {/* PHOTO SECTION ----------------------------- */}
        <View style={styles.imagesContainer}>
          {media?.ortofoto ? (
            <Image style={styles.image} src={media.ortofoto} />
          ) : (
            <View style={styles.placeholderImage} />
          )}

          {media?.fotoCopertina ? (
            <Image style={styles.image} src={media.fotoCopertina} />
          ) : (
            <View style={styles.placeholderImage} />
          )}
        </View>

        <View style={styles.captionsContainer}>
          <View style={styles.caption}>
            <Text style={styles.infoData}>Ortofoto</Text>
          </View>
          <View style={styles.caption}>
            <Text style={styles.infoData}>Foto</Text>
          </View>
        </View>
        {/* ------------------------------------------- */}

        {/* GENERAL DATA SECTION ----------------------------- */}
        <View style={styles.container}>
          <Text style={styles.titleH2}>Dati Generali</Text>

          <View style={{ display: 'flex', flexDirection: 'row' }}>
            <View style={{ width: '55%' }}>
              <Text style={styles.titleH3}>CARATTERISTICHE IDRAULICHE</Text>

              <View style={styles.flexContainer}>
                <Text style={styles.infoLabel}>Dimensione bacino</Text>
                <Text style={{ ...styles.infoData, ...styles.textForSymbol }}>
                  {dimensioniBacino || '-'}
                </Text>
              </View>

              <View style={styles.flexContainer}>
                <Text style={styles.infoLabel}>Tipologia reticolo</Text>
                <Text style={styles.infoData}>{tipologiaReticolo || '-'}</Text>
              </View>

              <View style={styles.flexContainer}>
                <Text style={styles.infoLabel}>Morfologia alveo</Text>
                <Text style={styles.infoData}>{morgologiaAlveo || '-'}</Text>
              </View>

              <View style={styles.flexContainer}>
                <Text style={styles.infoLabel}>Evoluzione alveo</Text>
                <Text style={styles.infoData}>{evoluzioneAlveo || '-'}</Text>
              </View>

              <View style={styles.flexContainer}>
                <Text
                  style={styles.infoLabel}
                >{`Tipologia corso d'acqua`}</Text>
              </View>
              <View style={styles.flexContainer}>
                <Text style={styles.infoDataMaxWidth}>
                  {tipologiaCorsoAcqua || '-'}
                </Text>
              </View>
            </View>

            <View style={{ width: '45%' }}>
              <Text style={styles.titleH3}>STRUTTURA</Text>

              <View style={styles.flexContainer}>
                <Text style={styles.infoLabel}>Lunghezza totale</Text>
                <Text style={styles.infoData}>
                  {generalData?.luceComplessivaEstesa
                    ? `${generalData.luceComplessivaEstesa} m`
                    : '-'}
                </Text>
              </View>

              <View style={styles.flexContainer}>
                <Text style={styles.infoLabel}>Luci idrauliche</Text>
                <Text style={styles.infoData}>{luciIdrauliche || '-'}</Text>
              </View>

              <View style={styles.flexContainer}>
                <Text style={styles.infoLabel}>Altezza utile</Text>
                <Text style={styles.infoData}>
                  {generalData?.altezzaUtileSottoImpalcato
                    ? `${generalData.altezzaUtileSottoImpalcato} m`
                    : '-'}
                </Text>
              </View>

              <View style={styles.flexContainer}>
                <Text style={styles.infoLabel}>Fondazioni</Text>
                <Text style={styles.infoData}>
                  {evidenzaProfonditaFondazioniGenerale || '-'}
                </Text>
              </View>

              <View style={styles.flexContainer}>
                <Text style={styles.infoLabel}>Parti che interferiscono</Text>
                <Text style={styles.infoData}>
                  {partiInterferisconoAlveo || '-'}
                </Text>
              </View>

              <View style={styles.flexContainer}>
                <Text style={styles.infoLabel}>
                  Larghezza pila o aggetto spalla
                </Text>
                <Text style={styles.infoData}>
                  {generalData?.larghezzaPilaOAggettoSpalla
                    ? `${generalData.larghezzaPilaOAggettoSpalla} m`
                    : '-'}
                </Text>
              </View>
            </View>
          </View>
        </View>
        {/* ----------------------------------------------------------- */}

        {/* PHENOMENA SECTION ------------------------------------------- */}
        <View
          style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
          }}
        >
          <View style={{ width: '49%' }}>
            <View style={[styles.container, styles.phenomenonContainer]}>
              <Text style={styles.titleH2}>Fenomeno di Sormonto</Text>
              {renderCdATagInSection(cdaClasses?.overlap)}
              <View style={styles.flexContainer}>
                <Text style={styles.infoLabel}>Franco idraulico Fp2 [m]</Text>
                <Text style={styles.infoData}>{francoIdraulico || '-'}</Text>
              </View>
              <View style={styles.flexContainer}>
                <Text style={styles.infoLabel}>
                  Evidente deposizione sedimenti
                </Text>
                <Text style={styles.infoData}>
                  {evidenteDeposizioneSedimenti || '-'}
                </Text>
              </View>
              <View style={styles.flexContainer}>
                <Text style={styles.infoLabel}>
                  Trasporto massivo di materiale vegetale
                </Text>
                <Text style={styles.infoData}>
                  {trasportoMassimoMaterialeVegetale || '-'}
                </Text>
              </View>
            </View>
          </View>

          <View style={{ width: '49%' }}>
            <View style={[styles.container, styles.phenomenonContainer]}>
              <Text style={styles.titleH2}>Fenomeni di Erosione</Text>
              {renderCdATagInSection(cdaClasses?.erosion)}
              <View style={styles.flexContainer}>
                <Text style={styles.infoLabel}>
                  Massima profondità di scavo ds totale [m]
                </Text>
                <Text style={styles.infoData}>
                  {erosion?.massimaProfonditaScavoTotale ?? '-'}
                </Text>
              </View>
              <View style={styles.flexContainer}>
                <Text style={styles.infoLabel}>
                  Profondità fondazione df rispetto all&rsquo;alveo [m]
                </Text>
                <Text style={styles.infoData}>
                  {erosion?.profonditaFondazioneRispettoAlveo ?? '-'}
                  {flagProfonditaFondazione
                    ? ` - ${flagProfonditaFondazione}`
                    : ''}
                </Text>
              </View>
              <View style={styles.flexContainer}>
                <Text style={styles.infoLabel}>
                  Evidenze sulla profondità delle fondazioni
                </Text>
                <Text style={styles.infoData}>
                  {evidenzaProfonditaFondazioniErosione || '-'}
                </Text>
              </View>
              {/* Issue #1029 — label troppo lunga per stare sulla stessa
              riga del valore: in un unico flexContainer (row) il valore si
              sovrapponeva alla seconda riga della label andata a capo.
              Stesso pattern già in uso sopra per "Tipologia corso d'acqua"
              (label e valore su due flexContainer separati, invece che
              affiancati). */}
              <View style={styles.flexContainer}>
                <Text style={styles.infoLabel}>
                  Protezione al piede delle pile e delle spalle del ponte
                </Text>
              </View>
              <View style={styles.flexContainer}>
                <Text style={styles.infoDataMaxWidth}>
                  {protezioneAlPiede || '-'}
                  {statoConservazioneProtezione
                    ? ` - ${statoConservazioneProtezione}`
                    : ''}
                </Text>
              </View>
            </View>
          </View>
        </View>
        {/* ----------------------------------------------------------- */}

        {/* ESITI SECTION (609/610, specifico L3) ----------------------- */}
        <View style={styles.container}>
          <Text style={[styles.titleH2, { textAlign: 'center' }]}>Esiti</Text>

          <View style={{ display: 'flex', flexDirection: 'row', gap: 10 }}>
            <View style={{ width: '65%' }}>
              <Text style={styles.titleH3}>Esito della valutazione</Text>
              {/* m.dangelo, 2026-09-22 — ID609 può avere più risposte
              flaggate: colonna (non più riga), una sotto l'altra. Resta
              valido mostrare SOLO le opzioni selezionate, non tutte e
              cinque (a differenza della riga a 5 pillole del riepilogo web,
              L3RecapContent.jsx/EsitoValutazionePillRow, che mostra sempre
              tutte le opzioni per dare il contesto della scala). */}
              <View
                style={{ display: 'flex', flexDirection: 'column', gap: 4 }}
              >
                {esitiSelezionati.length > 0 ? (
                  EsitoValutazioneL3Options.filter(option =>
                    esitiSelezionati.includes(option.value)
                  ).map(option => (
                    <View
                      key={option.value}
                      style={[styles.esitoBox, styles.esitoBoxActive]}
                    >
                      <Text
                        style={[styles.esitoBoxText, styles.esitoBoxTextActive]}
                      >
                        {option.label}
                      </Text>
                    </View>
                  ))
                ) : (
                  <Text style={styles.infoData}>-</Text>
                )}
              </View>
            </View>
            <View style={{ width: '35%' }}>
              <Text style={styles.titleH3}>Condizioni di transitabilità</Text>
              <View
                style={{
                  backgroundColor: condizioniTransitabilitaEntry.labelColor,
                  borderRadius: '12pt',
                  padding: '4pt',
                  width: '120pt',
                }}
              >
                <Text style={[styles.tagTextInSection, { color: 'black' }]}>
                  {condizioniTransitabilitaEntry.labelText}
                </Text>
              </View>
            </View>
          </View>
        </View>
        {/* ----------------------------------------------------------- */}

        {/* DATA FORM ------------------------------------------------- */}
        <View style={[styles.formData, { left: 16 }]}>
          <Text style={styles.infoData}>
            {`${
              formData?.denominazioneCommittente &&
              `${formData.denominazioneCommittente},`
            } ${formData?.indirizzo && `${formData.indirizzo},`} ${
              formData?.cap && `${formData.cap},`
            } ${formData?.citta && `${formData.citta},`} ${
              formData?.provincia && `(${formData.provincia})`
            }`}
          </Text>
        </View>
        <View style={[styles.formData, { right: 16, bottom: 16 }]}>
          <Image style={{ width: '80pt' }} src={LogoExport} />
        </View>
      </Page>
    </Document>
  );
};

export { SintesiL3Idraulico };

SintesiL3Idraulico.propTypes = {
  pdfData: PropTypes.object,
  formData: PropTypes.object,
};
